import React from 'react';
import { RadialRings, Icon } from '../../campaign-os';
import { CHANNEL_COLOR, channelSeries, overallOpenRate, totalReach } from '../data/channelChart';

/**
 * How a send split across To, CC and BCC, and how each field fared.
 *
 * Three shapes because there are three questions, and one shape cannot answer all of
 * them: concentric rings share a scale and so compare engagement, a stacked bar shows the
 * split, and a table carries the counts nobody can read off either.
 *
 * Shared by the campaign drawer, the dashboard and the analytics page so the same data
 * never gets two appearances. Two things differ between hosts: `onSelect`, because inside
 * a campaign a field is a filter you can click through to and on the portfolio pages it
 * is not; and `split`, which puts the table beside the rings instead of under them. That
 * is a prop rather than a breakpoint because the drawer is narrow inside a wide window —
 * the viewport cannot tell you how much room this component actually has.
 *
 * The rows beside the rings are a grid, not a flex line: the field name, its counts and
 * its rate each get a column of their own, so the three rates sit on one vertical edge and
 * can be compared by eye. In a flex line they drift with the length of the counts beside
 * them, which is what made this section hard to read.
 */
const num = (n) => Number(n || 0).toLocaleString('en-US');

const COLUMNS = [
  { key: 'sent', label: 'Sent' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'opened', label: 'Opened' },
  { key: 'clicked', label: 'Clicked' },
  { key: 'bounced', label: 'Bounced', alarming: true },
  { key: 'failed', label: 'Failed', alarming: true },
];

/**
 * One number in the table. Right-aligned in the monospaced face so the columns line up,
 * and coral the moment a count that means trouble stops being zero — the only colour in
 * the table, and so the only thing that pulls the eye.
 */
function FieldCell({ value, alarming = false }) {
  const tone = alarming && value > 0 ? 'text-cos-status-coral' : value > 0 ? 'text-cos-ink' : 'text-cos-ink-muted';
  return <td className={`mono px-1.5 py-2 text-right text-[12px] font-semibold tabular-nums ${tone}`}>{num(value)}</td>;
}

export function ChannelReach({ channels, onSelect, split = false }) {
  const series = channelSeries(channels || []);
  if (series.length === 0) return null;

  const reach = totalReach(channels || []);
  const overall = overallOpenRate(channels || []);
  const clickable = typeof onSelect === 'function';

  const Row = clickable ? 'button' : 'div';

  /** The whole send, so the table foots to the figures quoted everywhere else on the page. */
  const totals = COLUMNS.reduce((acc, col) => ({ ...acc, [col.key]: series.reduce((n, c) => n + (c[col.key] || 0), 0) }), {});

  /**
   * Where the send went, as one bar. Beside the rings it made the left column far taller than
   * the table across from it and left a band of empty card under that table; below the table it
   * fills that space and the two columns end together.
   */
  const composition = (
    <div
      className={
        split
          ? 'rounded-xl border border-cos-line-subtle bg-white/60 px-3 py-3'
          : 'mt-3 border-t border-cos-line-subtle px-2 pt-3'
      }
    >
      <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.07em] text-cos-ink-muted">
        How the {num(reach)} split
      </div>
      {/* One segment per field, hairline-separated so neighbouring colours stay distinct
          even where a field is a sliver of the send. */}
      <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-cos-beige-light/60">
        {series.map((c) => (
          <div
            key={c.kind}
            className="h-full border-r border-white/70 last:border-r-0"
            style={{ width: `${c.share}%`, background: c.color, minWidth: c.total > 0 ? 2 : 0 }}
            title={`${c.name}: ${num(c.total)} of ${num(reach)}`}
          />
        ))}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-cos-ink-muted">
        {series.map((c) => (
          <span key={c.kind} className="flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: c.color }} />
            {c.name}
            <span className="mono font-bold tabular-nums text-cos-ink">{c.share}%</span>
            <span className="text-cos-ink-muted">({num(c.total)})</span>
          </span>
        ))}
      </div>
    </div>
  );

  return (
    <div className={split ? 'flex flex-col gap-5 lg:flex-row lg:items-stretch' : ''}>
      <div
        className={`flex flex-col items-center gap-5 sm:flex-row sm:items-center sm:gap-6 ${
          split ? 'lg:w-[480px] lg:shrink-0 lg:justify-center' : ''
        }`}
      >
        <RadialRings
          rings={series.map((c) => ({ key: c.kind, label: c.name, value: c.rate, color: c.color }))}
          centerValue={`${overall}%`}
          centerLabel="opened overall"
        />

        <div className="w-full min-w-0 flex-1">
          {series.map((c) => (
            <Row
              key={c.kind}
              {...(clickable
                ? { type: 'button', onClick: () => onSelect(c.kind), title: `Show the ${c.name} recipients` }
                : {})}
              className={`group grid w-full grid-cols-[10px_minmax(0,1fr)_auto] items-center gap-x-2.5 gap-y-0.5 rounded-lg px-2 py-1.5 text-left ${
                clickable ? 'transition-colors hover:bg-white/70' : ''
              }`}
            >
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: c.color }} />
              <span className="text-[13px] font-semibold text-cos-ink">{c.name}</span>
              <span className="flex items-center justify-end gap-1">
                <span className="mono w-[46px] text-right text-[15px] font-bold tabular-nums text-cos-ink">
                  {c.rate}%
                </span>
                {clickable && (
                  <Icon
                    name="chevron_right"
                    className="text-[16px] text-cos-ink-muted opacity-0 transition-opacity group-hover:opacity-100"
                  />
                )}
              </span>
              {/* The counts take a line of their own: set beside the name they were the first
                  thing to be truncated in the narrow drawer, which is where they matter most. */}
              <span className="col-span-2 col-start-2 text-2xs text-cos-ink-muted">
                {num(c.total)} {c.total === 1 ? 'address' : 'addresses'}
                <span className="px-1">&middot;</span>
                {num(c.opened)} opened
              </span>
            </Row>
          ))}

          {!split && series.length > 1 && composition}
        </div>
      </div>

      {/* The rings rank the fields; the exact figure lives here, including the two counts
          that are never on the ladder at all - bounced and failed. */}
      <div className={split ? 'flex min-w-0 flex-1 flex-col gap-4' : 'mt-5'}>
        {/* Seven columns cannot fit a phone: below its own width the table scrolls sideways
            rather than letting the headings run into each other. */}
        <div className="overflow-x-auto rounded-xl border border-cos-line-subtle bg-white/60">
          <table className="w-full min-w-[520px] table-fixed border-collapse">
            <thead>
              <tr className="bg-white/70">
                <th className="w-[62px] px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-[0.06em] text-cos-ink-muted">
                  Field
                </th>
                {COLUMNS.map((col) => (
                  <th
                    key={col.key}
                    className="px-1.5 py-2 text-right text-[10px] font-semibold uppercase tracking-[0.06em] text-cos-ink-muted"
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {series.map((c) => (
                <tr key={c.kind} className="border-t border-cos-line-subtle">
                  <td className="px-2 py-2">
                    <span className="flex items-center gap-1.5">
                      <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: c.color }} />
                      <span className="text-[11px] font-bold uppercase tracking-[0.04em] text-cos-ink-secondary">
                        {c.name}
                      </span>
                    </span>
                  </td>
                  {COLUMNS.map((col) => (
                    <FieldCell key={col.key} value={c[col.key]} alarming={col.alarming} />
                  ))}
                </tr>
              ))}
            </tbody>
            {/* The send as a whole, so the columns foot to the figures quoted elsewhere. */}
            <tfoot>
              <tr className="border-t border-cos-line-subtle bg-white/70">
                <td className="px-2 py-2 text-[11px] font-bold uppercase tracking-[0.04em] text-cos-ink-secondary">
                  All
                </td>
                {COLUMNS.map((col) => (
                  <FieldCell key={col.key} value={totals[col.key]} alarming={col.alarming} />
                ))}
              </tr>
            </tfoot>
          </table>
        </div>
        {split && series.length > 1 && composition}
      </div>
    </div>
  );
}

export { CHANNEL_COLOR };
export default ChannelReach;
